import React, { useState, useEffect } from "react";
import { useForm, validators } from "@hooks";
import classNames from "classnames";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { useNavigate, useParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { Computer } from "@resources";

const ResetPassword = () => {

    const navigate = useNavigate();
    const params = useParams();
    const { setSpinnerShow } = useSpinnerContext();
    const { sessionId } = params;
    const [isValid, setIsValid] = useState(null);
    const [show, setShow] = useState({ password: false, confirm: false });
    const initialState = { password: ``, confirm: `` };

    useEffect(() => {
        (async () => {

            setSpinnerShow(true);
            try {
                await sunboundPayProxy.resetPasswordCheck(sessionId);
                setIsValid(true);
            } catch (error) {
                setIsValid(false);
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, []);

    const validations = {
        password: (val) => validators.validatePassword(val),
        confirm: (val, values) => compare(val, values[`password`]) || { confirm: `Passwords do not match` }
    };

    const toggleShow = (name) => {
        const update = { ...show, [name]: !show[name] };
        setShow(update);
    };

    const compare = (val, match) => val === match;

    const submit = async () => {
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.resetPassword(values.password, sessionId);
            navigate(`/reset-password-success`);
        } catch (error) {
            // setFormState(`Unable to process your request, please contact ${Constants.Help.Email} for support`);
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const { values, errors, changeHandler, submitHandler } = useForm(initialState, {}, validations, submit);

    return (
        <div className="page-content-wrapper">
            {
                isValid &&
                <>
                    <h1 className="page-header">Reset your Password</h1>
                    <span className="page-subheader">To reset your password for your Sunbound account please type and confirm a new password below</span>

                    <form className="space-y-5" method="POST" onSubmit={submitHandler} >
                        <div className="flex flex-col bg-sb-frame rounded-xl shadow-sb p-5 space-y-3">
                            <div>
                                <label htmlFor="password" className="font-semibold text-gray-700">Password</label>
                                <div className="relative mt-1 rounded-md shadow-sm">
                                    <input
                                        type={show.password ? `text` : `password`}
                                        maxLength="20"
                                        name="password"
                                        id="password"
                                        className={classNames(`pr-16 block w-full rounded-md shadow-md focus:outline-none text-gray-600`, { "input-error": errors.password })}
                                        value={values.password}
                                        onChange={changeHandler}
                                        aria-invalid="true"
                                        aria-describedby="email-error" />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        {
                                            errors.password &&
                                            <ExclamationCircleIcon className="fill-red-500 h-6 pr-1" />
                                        }
                                        <div className="cursor-pointer" onClick={() => toggleShow(`password`)}>
                                            {
                                                !show.password &&
                                                <EyeIcon className="h-6" />
                                            }
                                            {
                                                show.password &&
                                                <EyeSlashIcon className="h-6" />
                                            }
                                        </div>
                                    </div>
                                </div>
                                <span className="text-gray-600 text-sm" >Use 9-20 characters and include a mix of upper and lowercase letters, numbers, and special characters. Do not use spaces.</span>
                                {
                                    errors.password &&
                                    <ul className="list-disc ml-3">
                                        {errors.password.map((e, index) => <li key={index} className="text-sm text-red-600" id="email-error">{e}</li>)}

                                    </ul>

                                }
                            </div>
                            <div>
                                <label htmlFor="confirm" className="font-semibold text-gray-700">Confirm Password</label>
                                <div className="relative mt-1 rounded-md shadow-sm">
                                    <input
                                        type={show.confirm ? `text` : `password`}
                                        maxLength="20"
                                        name="confirm"
                                        id="confirm"
                                        className={classNames(`pr-16 block w-full rounded-md shadow-md focus:outline-none text-gray-600`, { "input-error": errors.confirm })}
                                        value={values.confirm}
                                        onChange={changeHandler}
                                        aria-invalid="true"
                                        aria-describedby="email-error" />
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                        {
                                            errors.confirm &&
                                            <ExclamationCircleIcon className="fill-red-500 h-6 pr-1" />
                                        }
                                        <div className="cursor-pointer" onClick={() => toggleShow(`confirm`)}>
                                            {
                                                !show.confirm &&
                                                <EyeIcon className="h-6" />
                                            }
                                            {
                                                show.confirm &&
                                                <EyeSlashIcon className="h-6" />
                                            }
                                        </div>
                                    </div>
                                </div>
                                {
                                    errors.confirm &&
                                    <p className="mt-2 text-sm text-red-600" id="email-error">{errors.confirm}</p>
                                }
                            </div>
                        </div>
                        <button type="submit" className="btn btn-primary">Reset Password</button>
                    </form>
                </>
            }
            {
                !isValid &&
                <>
                    <Computer className="h-56" />
                    <p className="page-header">Oops! There’s a problem with that link</p>
                    <p className="page-header">If you still need to reset your password, go back to the login screen and request another reset link.</p>
                    <p className="page-header">Remember to use the link as soon as you get it, because it will expire one hour after it is sent</p>
                    <div className="page-action">
                        <button type="button" onClick={() => navigate(`/`)} className="btn btn-primary h-14 mt-7 text-lg font-bold rounded-full">Back to login</button>
                    </div>
                </>
            }
        </div>
    );
};

export default ResetPassword;